import { Graphics } from 'pixi.js';

const DESK_WOOD = 0x5C4033;
const DESK_DARK = 0x3E2723;
const DESK_LEG = 0x2E1F14;
const SCREEN_BG = 0x1A1D2E;
const SCREEN_GLOW = 0x22C55E;
const KEYBOARD = 0x374151;
const CHAIR_DARK = 0x1F2937;
const CHAIR_MID = 0x374151;
const CHAIR_LIGHT = 0x4B5563;

/**
 * Back layer: ALL furniture drawn BEHIND the avatar.
 *
 * New spatial layout (y = grid center of the desk/employee unit):
 *
 *   y-44 to y-20  : Monitor screen
 *   y-20 to y-12  : Monitor stand
 *   y-12 to y-8   : Monitor base (on desk surface)
 *   y-8  to y+6   : Desk surface + keyboard
 *   y+6  to y+12  : Desk front edge
 *   y+12 to y+28  : Desk legs
 *   --- avatar is at y+30 (drawn on avatar layer) ---
 *   y+40 to y+50  : Chair back
 *   y+50 to y+64  : Chair seat
 *
 * Everything is in deskBack so nothing collides with the walking avatar.
 * The avatar sits BELOW the desk (visually in front from camera POV).
 */
export function drawDeskBack(g: Graphics, x: number, y: number) {
  // --- Monitor screen ---
  const mw = 36;
  const mh = 24;
  const mx = x - mw / 2;
  const my = y - 44;

  g.roundRect(mx, my, mw, mh, 2);
  g.fill(SCREEN_BG);
  g.stroke({ width: 2, color: 0x4B5563 });

  // Screen chart lines
  g.moveTo(mx + 4, my + mh - 6);
  g.lineTo(mx + mw * 0.3, my + 6);
  g.lineTo(mx + mw * 0.5, my + mh - 8);
  g.lineTo(mx + mw * 0.7, my + 4);
  g.lineTo(mx + mw - 4, my + mh - 6);
  g.stroke({ width: 1.5, color: SCREEN_GLOW });

  // --- Monitor stand (connects screen to desk) ---
  g.rect(x - 3, my + mh, 6, 8);
  g.fill(0x4B5563);

  // --- Monitor base (sits on desk surface) ---
  g.rect(x - 8, my + mh + 8, 16, 4);
  g.fill(0x4B5563);

  // --- Desk surface ---
  const dw = 96;
  g.roundRect(x - dw / 2, y - 8, dw, 14, 3);
  g.fill(DESK_WOOD);
  g.stroke({ width: 1, color: DESK_DARK });

  // Keyboard on desk surface
  g.roundRect(x - 12, y - 6, 24, 8, 1);
  g.fill(KEYBOARD);
  // Key rows
  for (let ki = 0; ki < 4; ki++) {
    g.rect(x - 10 + ki * 6, y - 4, 4, 3);
    g.fill(0x4B5563);
  }

  // --- Desk front edge ---
  g.rect(x - dw / 2, y + 6, dw, 6);
  g.fill(DESK_DARK);

  // --- Desk legs ---
  g.rect(x - dw / 2 + 4, y + 12, 4, 16);
  g.fill(DESK_LEG);
  g.rect(x + dw / 2 - 8, y + 12, 4, 16);
  g.fill(DESK_LEG);

  // Chair removed — was visually blocking avatars
}

/**
 * Front layer: empty now — all furniture is in deskBack to avoid
 * collision issues with walking avatars.
 */
export function drawDeskFront(_g: Graphics, _x: number, _y: number) {
  // No-op: everything drawn in deskBack
}

/** Legacy single-draw (kept for reference) */
export function drawDesk(g: Graphics, x: number, y: number) {
  drawDeskBack(g, x, y);
  drawDeskFront(g, x, y);
}
